import React, { useState, useMemo } from 'react';
import { UsersIcon, SunIcon, CalendarClockIcon, PackageIcon, KeyIcon, SearchIcon, PlusIcon, CheckIcon, XIcon, AlertTriangleIcon, FileTextIcon, FileCheckIcon, LockIcon, ChevronRightIcon, BuildingIcon, UserIcon, MinusCircleIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { ActivityRegistrations } from './loisirs/ActivityRegistrations';
import { BookingsSection } from './loisirs/BookingsSection';
import { paymentMeta } from './loisirs/bookingHelpers';
import { ACTIVITIES, CAMPS, CAMP_REGISTRATIONS, EQUIPMENT, EQUIPMENT_LOANS, ACCESS_POINTS, ACCESS_EVENTS } from '../data/loisirs';
import { BOOKING_FILES, CONTRACTS } from '../data/windsor';
import { resolvePayment, type FileStatus } from '../types/loisirs';
type Section = 'inscriptions' | 'camps' | 'reservations' | 'equipements' | 'acces';

/** Small stat shown at the top of each operational section. */
function Stat({
  label,
  value,
  hint,
  tone = 'neutral'
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: 'neutral' | 'warning' | 'positive';
}) {
  return <div className="bg-white rounded-xl border border-slate-200 px-4 py-3.5" data-id="element-3445" data-mp="18n3hl25q6519">
      <p className="text-xs font-medium text-slate-500" data-id="element-3446" data-mp="tvkz8xpe7yeq">{label}</p>
      <p className={`text-2xl font-bold font-heading mt-0.5 ${tone === 'warning' ? 'text-amber-600' : tone === 'positive' ? 'text-emerald-600' : 'text-[#0F172A]'}`} data-id="element-3447" data-mp="yufi1znd1myu">
        {value}
      </p>
      {hint && <p className="text-xs text-slate-400 mt-0.5" data-id="element-3448" data-mp="axrxhyqi9uix">{hint}</p>}
    </div>;
}
interface GererProps {
  /** Section driven from the module shell (guided demo); falls back to local state. */
  section?: Section;
  onSectionChange?: (section: Section) => void;
}
export function LoisirsGerer({
  section: sectionProp,
  onSectionChange
}: GererProps = {}) {
  const {
    t,
    lang
  } = useLanguage();
  const locale = lang === 'fr' ? 'fr-CA' : 'en-CA';
  const [localSection, setLocalSection] = useState<Section>('inscriptions');
  const section = sectionProp ?? localSection;
  const setSection = (next: Section) => {
    setLocalSection(next);
    onSectionChange?.(next);
  };
  const [query, setQuery] = useState('');
  const [creatingActivity, setCreatingActivity] = useState(false);
  const [creatingBooking, setCreatingBooking] = useState(false);
  const money = (value: number) => value.toLocaleString(locale, {
    style: 'currency',
    currency: 'CAD',
    maximumFractionDigits: 0
  });
  const sections: {
    id: Section;
    label: string;
    icon: typeof UsersIcon;
    count: number;
  }[] = [{
    id: 'inscriptions',
    label: t('Inscriptions', 'Registrations'),
    icon: UsersIcon,
    count: ACTIVITIES.length
  }, {
    id: 'camps',
    label: t('Camps de jour', 'Day camps'),
    icon: SunIcon,
    count: CAMP_REGISTRATIONS.filter(r => r.healthForm !== 'complete').length
  }, {
    id: 'reservations',
    label: t('Réservations', 'Bookings'),
    icon: CalendarClockIcon,
    count: BOOKING_FILES.filter(b => b.status === 'brouillon').length + CONTRACTS.filter(c => c.status === 'a_generer' || c.status === 'en_retard').length
  }, {
    id: 'equipements',
    label: t('Équipements', 'Equipment'),
    icon: PackageIcon,
    count: EQUIPMENT_LOANS.filter(l => l.status === 'overdue').length
  }, {
    id: 'acces',
    label: t('Contrôle d’accès', 'Access control'),
    icon: KeyIcon,
    count: ACCESS_EVENTS.filter(e => e.result === 'denied').length
  }];
  const fileMeta: Record<FileStatus, {
    label: string;
    className: string;
  }> = {
    complete: {
      label: t('Complète', 'Complete'),
      className: 'bg-emerald-50 text-emerald-700 border-emerald-200'
    },
    incomplete: {
      label: t('Incomplète', 'Incomplete'),
      className: 'bg-amber-50 text-amber-700 border-amber-200'
    },
    missing: {
      label: t('Manquante', 'Missing'),
      className: 'bg-red-50 text-red-700 border-red-200'
    }
  };
  const searchPlaceholder: Record<Section, string> = {
    inscriptions: t('Rechercher une activité…', 'Search activities…'),
    camps: t('Rechercher un enfant ou un parent…', 'Search a child or guardian…'),
    reservations: t('Rechercher un client, un n° ou une ressource…', 'Search a client, number or resource…'),
    equipements: t('Rechercher un équipement…', 'Search equipment…'),
    acces: t('Rechercher un citoyen ou une carte…', 'Search a citizen or card…')
  };
  const primaryAction: Record<Section, string> = {
    inscriptions: t('Nouvelle activité', 'New activity'),
    camps: t('Inscrire un enfant', 'Register a child'),
    reservations: t('Nouvelle réservation', 'New booking'),
    equipements: t('Nouveau prêt', 'New loan'),
    acces: t('Émettre un accès', 'Issue an access')
  };
  return <div className="space-y-6 animate-in fade-in duration-300" data-id="element-3449" data-mp="2ik9uo77b5h8g">
      {/* Section navigation */}
      <div className="flex flex-wrap items-center gap-2" data-id="element-3450" data-mp="2q3sxxz3zc78h">
        {sections.map(s => {
        const Icon = s.icon;
        const active = section === s.id;
        return <button key={s.id} onClick={() => {
          setSection(s.id);
          setQuery('');
        }} className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-medium border transition-colors ${active ? 'bg-[#0F172A] text-white border-[#0F172A]' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`} data-id="element-3451" data-mp="2h2ut5elpvvou">
              <Icon className={`w-4 h-4 ${active ? 'text-[#4F46E5]' : 'text-slate-400'}`} data-id="element-3452" data-mp="33zoc5rnfou5j" />
              {s.label}
              {s.count > 0 && <span className={`text-xs font-bold ${active ? 'text-slate-300' : 'text-slate-400'}`} data-id="element-3453" data-mp="d61pbq3bvfc">
                  {s.count}
                </span>}
            </button>;
      })}
      </div>

      {/* Toolbar — hidden while a creation form is open. */}
      {!creatingActivity && !creatingBooking && <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between" data-id="element-3454" data-mp="3sdzqdzt8fojf">
        <div className="relative flex-1 sm:max-w-sm" data-id="element-3455" data-mp="3nemvcrs7jb5r">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" data-id="element-3456" data-mp="3ng7qaiifow45" />
          <input type="search" value={query} onChange={e => setQuery(e.target.value)} placeholder={searchPlaceholder[section]} aria-label={searchPlaceholder[section]} className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-1 focus:ring-[#4F46E5] focus:border-[#4F46E5]" data-id="element-3457" data-mp="3iltpg0l05ws3" />
        </div>
        <button onClick={() => {
        if (section === 'inscriptions') setCreatingActivity(true);
        if (section === 'reservations') setCreatingBooking(true);
      }} className="shrink-0 inline-flex items-center gap-2 px-4 py-2 bg-[#4F46E5] hover:bg-[#4338CA] text-white text-sm font-semibold rounded-lg transition-colors shadow-sm" data-id="element-3458" data-mp="2pldrqp89esif">
          <PlusIcon className="w-4 h-4" data-id="element-3459" data-mp="34g1tbsajh6sr" />
          {primaryAction[section]}
        </button>
      </div>}

      {/* ---------------------------------------------------- INSCRIPTIONS */}
      {section === 'inscriptions' && <ActivityRegistrations query={query} creating={creatingActivity} onCloseCreate={() => setCreatingActivity(false)} data-id="element-3460" data-mp="2gsjxnlo5loki" />}

      {/* ----------------------------------------------------------- CAMPS */}
      {section === 'camps' && <div className="space-y-4" data-id="element-3461" data-mp="1t2zep2s02rb8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-id="element-3462" data-mp="1t1y0ipfotopa">
            <Stat label={t('Enfants inscrits', 'Children registered')} value={`${CAMPS.reduce((s, c) => s + c.registered, 0)}`} hint={`${CAMPS.reduce((s, c) => s + c.capacity, 0)} ${t('places', 'spots')}`} data-id="element-3463" data-mp="379609s89vq5v" />
            <Stat label={t('Fiches santé à compléter', 'Health forms to complete')} value={`${CAMPS.reduce((s, c) => s + (c.registered - c.healthFormsComplete), 0)}`} tone="warning" hint={t('obligatoires avant le début', 'mandatory before start')} data-id="element-3464" data-mp="3h5slwj8fal8y" />
            <Stat label={t('Relevés 24 émis', 'RL-24 slips issued')} value={`${CAMPS.reduce((s, c) => s + c.rl24Issued, 0)}`} tone="positive" hint={t('transmis à Revenu Québec', 'filed with Revenu Québec')} data-id="element-3465" data-mp="3sz8aiz7zhcx" />
            <Stat label={t('Camps actifs', 'Active camps')} value={`${CAMPS.length}`} hint={t('été 2026', 'summer 2026')} data-id="element-3466" data-mp="dp2b7r1wbk0x" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3" data-id="element-3467" data-mp="sen3l2t51vow">
            {CAMPS.map(camp => {
          const missing = camp.registered - camp.healthFormsComplete;
          return <div key={camp.id} className="bg-white rounded-xl border border-slate-200 p-5" data-id="element-3468" data-mp="ihs8yuzzkffw">
                  <h3 className="text-base font-semibold text-[#0F172A]" data-id="element-3469" data-mp="8pmi5olh042a">
                    {camp.name[lang]}
                  </h3>
                  <p className="text-xs text-slate-500 mt-0.5" data-id="element-3470" data-mp="di5uki8f8t5i">
                    {camp.venue[lang]} · {camp.ageRange} {t('ans', 'yrs')} ·{' '}
                    {camp.weeks} {t('semaines', 'weeks')}
                  </p>
                  <div className="mt-3 space-y-2" data-id="element-3471" data-mp="4lwur2gj7siu">
                    <div data-id="element-3472" data-mp="3lxfupksr3oan">
                      <div className="flex items-center justify-between text-xs mb-1" data-id="element-3473" data-mp="3lx6cijdy6lfi">
                        <span className="text-slate-500" data-id="element-3474" data-mp="3quzknhyt9pif">
                          {t('Inscriptions', 'Registrations')}
                        </span>
                        <span className="font-medium text-slate-700" data-id="element-3475" data-mp="meceslv12a5p">
                          {camp.registered} / {camp.capacity}
                        </span>
                      </div>
                      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden" data-id="element-3476" data-mp="cgfzriyyyv2b">
                        <div className="h-full bg-[#4F46E5]" style={{
                    width: `${camp.registered / camp.capacity * 100}%`
                  }} data-id="element-3477" data-mp="ci98d3t57pcy" />
                      </div>
                    </div>
                    <div className="flex items-center gap-3 pt-1" data-id="element-3478" data-mp="vf0qvb0v20fy">
                      <span className="inline-flex items-center gap-1.5 text-xs text-slate-600" data-id="element-3479" data-mp="vdh73afzx79f">
                        <FileTextIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-3480" data-mp="1j3jw1hom5x3t" />
                        {camp.healthFormsComplete}
                        {missing > 0 && <span className="text-amber-600 font-medium" data-id="element-3481" data-mp="38kpa3alcp25z">
                            (−{missing})
                          </span>}
                      </span>
                      <span className="inline-flex items-center gap-1.5 text-xs text-slate-600" data-id="element-3482" data-mp="3iaiadfg1luux">
                        <FileCheckIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-3483" data-mp="3de1mmmcr9iid" />
                        {camp.rl24Issued} {t('RL-24', 'RL-24')}
                      </span>
                    </div>
                  </div>
                </div>;
        })}
          </div>

          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-id="element-3484" data-mp="2yo2nohddm31u">
            <div className="px-5 py-4 border-b border-slate-100" data-id="element-3485" data-mp="2ulpipv6ge964">
              <h3 className="text-base font-semibold text-[#0F172A]" data-id="element-3486" data-mp="2uoljdr9orby9">
                {t('Dossiers d’inscription', 'Registration files')}
              </h3>
              <p className="text-xs text-slate-500 mt-0.5" data-id="element-3487" data-mp="9v8cykn3ldth">
                {t('Fiche santé et relevé 24 par enfant', 'Health form and RL-24 slip per child')}
              </p>
            </div>
            <div className="overflow-x-auto" data-id="element-3488" data-mp="3coo9t1sjbgb2">
              <table className="w-full text-sm text-left" data-id="element-3489" data-mp="3cpzfj75jiy4t">
                <thead className="text-xs text-slate-500 uppercase bg-slate-50" data-id="element-3490" data-mp="3gpzt6pktf5at">
                  <tr data-id="element-3491" data-mp="3gnx3k6nkre6m">
                    <th className="px-5 py-3" data-id="element-3492" data-mp="2k0iq8id7vxct">{t('Enfant', 'Child')}</th>
                    <th className="px-5 py-3" data-id="element-3493" data-mp="2jy5zbfphecnm">{t('Camp', 'Camp')}</th>
                    <th className="px-5 py-3" data-id="element-3494" data-mp="2ozlhn009y52b">{t('Fiche santé', 'Health form')}</th>
                    <th className="px-5 py-3" data-id="element-3495" data-mp="2szvoeevw4zi8">{t('Relevé 24', 'RL-24')}</th>
                    <th className="px-5 py-3" data-id="element-3496" data-mp="2xrx64gbtvifn">{t('Montant', 'Amount')}</th>
                    <th className="px-5 py-3" data-id="element-3497" data-mp="219hf6vytobcw">{t('Suivi', 'Follow-up')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100" data-id="element-3498" data-mp="25aktvf54w1cj">
                  {CAMP_REGISTRATIONS.filter(r => {
                const q = query.trim().toLowerCase();
                return q ? r.childName.toLowerCase().includes(q) || r.guardian.toLowerCase().includes(q) : true;
              }).map(reg => {
                const health = fileMeta[reg.healthForm];
                return <tr key={reg.id} className="hover:bg-slate-50/60" data-id="element-3499" data-mp="1esfvcozmmegi">
                        <td className="px-5 py-3.5" data-id="element-3500" data-mp="1euk7fu7oijrv">
                          <p className="font-medium text-slate-900" data-id="element-3501" data-mp="1a0y1slzxng5k">
                            {reg.childName}
                          </p>
                          <p className="text-xs text-slate-500" data-id="element-3502" data-mp="150ju5528u882">
                            {reg.childAge} {t('ans', 'yrs')} · {reg.guardian}
                          </p>
                        </td>
                        <td className="px-5 py-3.5 text-slate-600" data-id="element-3503" data-mp="w42htyi584aq">
                          <p data-id="element-3504" data-mp="r3n6fchf96ve">{reg.campName[lang]}</p>
                          <p className="text-xs text-slate-400" data-id="element-3505" data-mp="2r5mr7er4z69p">
                            {reg.weeks} {t('semaines', 'weeks')}
                          </p>
                        </td>
                        <td className="px-5 py-3.5" data-id="element-3506" data-mp="2hfkyuarr31oz">
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium border ${health.className}`} data-id="element-3507" data-mp="2hf1tdtjae5ru">
                            {health.label}
                          </span>
                        </td>
                        <td className="px-5 py-3.5" data-id="element-3508" data-mp="2ch9qk4h3sz8v">
                          {reg.rl24 === 'issued' ? <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700" data-id="element-3509" data-mp="1yia1wwtlckcp">
                              <CheckIcon className="w-3.5 h-3.5" data-id="element-3510" data-mp="23g21md0h9arg" />
                              {t('Émis', 'Issued')}
                            </span> : reg.rl24 === 'pending' ? <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700" data-id="element-3511" data-mp="1mcinlvvhcnm">
                              <AlertTriangleIcon className="w-3.5 h-3.5" data-id="element-3512" data-mp="1h7elzkjozar8" />
                              {t('À émettre', 'To issue')}
                            </span> : <span className="inline-flex items-center gap-1 text-xs text-slate-400" data-id="element-3513" data-mp="1r5kn3zxoccv1">
                              <MinusCircleIcon className="w-3.5 h-3.5" data-id="element-3514" data-mp="24wz7ob78cm47" />
                              {t('Non admissible', 'Not eligible')}
                            </span>}
                        </td>
                        <td className="px-5 py-3.5" data-id="element-3515" data-mp="2eveu40vbb77d">
                          <p className="font-medium text-slate-900" data-id="element-3516" data-mp="2essoi6rzf1rs">
                            {money(reg.amount)}
                          </p>
                          <span className={`inline-flex mt-0.5 px-2 py-0.5 rounded-full text-xs font-medium border whitespace-nowrap ${paymentMeta(resolvePayment(reg).status).className}`} data-id="element-3517" data-mp="ki5k87yutjol">
                            {paymentMeta(resolvePayment(reg).status)[lang]}
                          </span>
                        </td>
                        <td className="px-5 py-3.5 max-w-xs" data-id="element-3518" data-mp="1e8xx1lwfnd6y">
                          {reg.alerts.length === 0 ? <span className="text-xs text-slate-400" data-id="element-3519" data-mp="1o50uwfk94po3">—</span> : <ul className="space-y-1" data-id="element-3520" data-mp="1s6e511udnf0t">
                              {reg.alerts.map((alert, i) => <li key={i} className="flex items-start gap-1.5 text-xs text-amber-700" data-id="element-3521" data-mp="1wx2ukbmhy1xb" data-mpi={i}>
                                  <AlertTriangleIcon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" data-id="element-3522" data-mp="26wla8fvdj1jk" />
                                  {alert[lang]}
                                </li>)}
                            </ul>}
                        </td>
                      </tr>;
              })}
                </tbody>
              </table>
            </div>
          </div>
        </div>}

      {/* ---------------------------------------------------- RÉSERVATIONS */}
      {section === 'reservations' && <BookingsSection query={query} creating={creatingBooking} onCloseCreate={() => setCreatingBooking(false)} data-id="element-3523" data-mp="iy10nltscnkw" />}

      {/* ----------------------------------------------------- ÉQUIPEMENTS */}
      {section === 'equipements' && <div className="space-y-4" data-id="element-3524" data-mp="494u5t7dnime">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-id="element-3525" data-mp="483j3oqd2w4o">
            <Stat label={t('Articles en prêt', 'Items on loan')} value={`${EQUIPMENT.reduce((s, e) => s + e.onLoan, 0)}`} hint={t('auprès des organismes', 'with organizations')} data-id="element-3526" data-mp="11osilbru9maw" />
            <Stat label={t('Retours en retard', 'Overdue returns')} value={`${EQUIPMENT_LOANS.filter(l => l.status === 'overdue').length}`} tone="warning" hint={t('relance à faire', 'follow-up needed')} data-id="element-3527" data-mp="5964lzci88md" />
            <Stat label={t('Dépôts détenus', 'Deposits held')} value={money(EQUIPMENT_LOANS.filter(l => l.status !== 'returned').reduce((s, l) => s + (EQUIPMENT.find(e => e.name.fr === l.equipment.fr)?.deposit ?? 0), 0))} hint={t('remboursables au retour', 'refundable on return')} data-id="element-3528" data-mp="17kuumv9oage" />
            <Stat label={t('Catégories', 'Categories')} value={`${new Set(EQUIPMENT.map(e => e.category.fr)).size}`} hint={t('au catalogue de prêt', 'in the loan catalogue')} data-id="element-3529" data-mp="2zpgmqd3ttn4b" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start" data-id="element-3530" data-mp="2x3qt5o7o299b">
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-id="element-3531" data-mp="2s5pa33a8q9xu">
              <div className="px-5 py-4 border-b border-slate-100" data-id="element-3532" data-mp="365735uukk9d0">
                <h3 className="text-base font-semibold text-[#0F172A]" data-id="element-3533" data-mp="317f1i944h8vm">
                  {t('Inventaire', 'Inventory')}
                </h3>
                <p className="text-xs text-slate-500 mt-0.5" data-id="element-3534" data-mp="2di46z9dd8apx">
                  {t('Prêt réservé aux organismes reconnus', 'Loans reserved for recognized organizations')}
                </p>
              </div>
              <ul className="divide-y divide-slate-100" data-id="element-3535" data-mp="2igfg1hvu6h73">
                {EQUIPMENT.filter(e => {
              const q = query.trim().toLowerCase();
              return q ? e.name[lang].toLowerCase().includes(q) : true;
            }).map(item => <li key={item.id} className="px-5 py-3.5 flex items-center gap-4 hover:bg-slate-50/60" data-id="element-3536" data-mp="m7tuwimggwvk">
                    <div className="min-w-0 flex-1" data-id="element-3537" data-mp="habszc6uvhx2">
                      <p className="text-sm font-medium text-slate-800" data-id="element-3538" data-mp="vy5c36v5h2ke">
                        {item.name[lang]}
                      </p>
                      <p className="text-xs text-slate-400" data-id="element-3539" data-mp="r5c8wnfby2af">
                        {item.category[lang]}
                        {item.deposit > 0 && <>
                            {' · '}
                            {t('dépôt ', 'deposit ')}
                            {money(item.deposit)}
                          </>}
                      </p>
                    </div>
                    <div className="text-right shrink-0" data-id="element-3540" data-mp="19vll8q4tzafu">
                      <p className={`text-sm font-bold ${item.available === 0 ? 'text-red-600' : 'text-[#0F172A]'}`} data-id="element-3541" data-mp="3gonopvcu43j3">
                        {item.available} / {item.total}
                      </p>
                      <p className="text-[11px] text-slate-400" data-id="element-3542" data-mp="1w4ileqnw6ped">
                        {t('disponibles', 'available')}
                      </p>
                    </div>
                  </li>)}
              </ul>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-id="element-3543" data-mp="18g92pxlz6hrg">
              <div className="px-5 py-4 border-b border-slate-100" data-id="element-3544" data-mp="18ilqireezmcf">
                <h3 className="text-base font-semibold text-[#0F172A]" data-id="element-3545" data-mp="1dgx0d24plwun">
                  {t('Prêts en cours', 'Active loans')}
                </h3>
                <p className="text-xs text-slate-500 mt-0.5" data-id="element-3546" data-mp="13gwjdmubbstm">
                  {t('Sorties et retours prévus', 'Check-outs and expected returns')}
                </p>
              </div>
              <ul className="divide-y divide-slate-100" data-id="element-3547" data-mp="2xv735gw1vwsj">
                {EQUIPMENT_LOANS.map(loan => <li key={loan.id} className="px-5 py-3.5 hover:bg-slate-50/60" data-id="element-3548" data-mp="2wdqowtpg9bey">
                    <div className="flex items-start justify-between gap-3" data-id="element-3549" data-mp="2rex7iq71l09i">
                      <div className="min-w-0" data-id="element-3550" data-mp="2rdm6uz8w88mm">
                        <p className="text-sm font-medium text-slate-800" data-id="element-3551" data-mp="2mfax0oilly4e">
                          {loan.quantity} × {loan.equipment[lang]}
                        </p>
                        <p className="text-xs text-slate-500 mt-0.5" data-id="element-3552" data-mp="3f4i44bw4bw19">
                          {loan.organization}
                        </p>
                        <p className="text-xs text-slate-400 mt-0.5" data-id="element-3553" data-mp="3a6gbaz8w4icl">
                          {t('Retour prévu le ', 'Due back on ')}
                          {new Date(loan.dueDate).toLocaleDateString(locale, {
                      day: 'numeric',
                      month: 'short'
                    })}
                        </p>
                      </div>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium border shrink-0 ${loan.status === 'overdue' ? 'bg-red-50 text-red-700 border-red-200' : loan.status === 'out' ? 'bg-indigo-50 text-[#4F46E5] border-indigo-100' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}`} data-id="element-3554" data-mp="3t40xtzo6n4u1">
                        {loan.status === 'overdue' ? t('En retard', 'Overdue') : loan.status === 'out' ? t('Sorti', 'Out') : t('Retourné', 'Returned')}
                      </span>
                    </div>
                  </li>)}
              </ul>
            </div>
          </div>
        </div>}

      {/* ----------------------------------------------------------- ACCÈS */}
      {section === 'acces' && <div className="space-y-4" data-id="element-3555" data-mp="2m7qq21yxj55o">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3" data-id="element-3556" data-mp="28mkxuahchgj0">
            {ACCESS_POINTS.map(point => {
          const rate = point.maxOccupancy ? Math.round(point.occupancy / point.maxOccupancy * 100) : 0;
          return <div key={point.id} className={`rounded-xl border p-5 ${point.online ? 'bg-white border-slate-200' : 'bg-slate-50 border-slate-200'}`} data-id="element-3557" data-mp="2ddji1l5geyrj">
                  <div className="flex items-start justify-between gap-2 mb-2" data-id="element-3558" data-mp="1zmeql6c8pcsa">
                    <div className="min-w-0" data-id="element-3559" data-mp="1zkbr7vp767aw">
                      <p className="text-sm font-semibold text-[#0F172A] truncate" data-id="element-3560" data-mp="1umjv0phirz0w">
                        {point.name[lang]}
                      </p>
                      <p className="text-xs text-slate-500" data-id="element-3561" data-mp="362qpxaqst0pg">{point.venue[lang]}</p>
                    </div>
                    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold border shrink-0 ${point.online ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-700 border-red-200'}`} data-id="element-3562" data-mp="3dm9ia7lxm6ap">
                      <span className={`w-1.5 h-1.5 rounded-full ${point.online ? 'bg-emerald-500' : 'bg-red-500'}`} data-id="element-3563" data-mp="7d1smqh9esj" />
                      {point.online ? t('En ligne', 'Online') : t('Hors ligne', 'Offline')}
                    </span>
                  </div>

                  {point.online ? <>
                      <div className="flex items-baseline gap-2" data-id="element-3564" data-mp="j5z5m9ddpk05">
                        <span className={`text-2xl font-bold font-heading ${rate >= 85 ? 'text-amber-600' : 'text-[#0F172A]'}`} data-id="element-3565" data-mp="j4edsrig8f5z">
                          {point.occupancy}
                        </span>
                        <span className="text-sm text-slate-400" data-id="element-3566" data-mp="orzod8quripy">
                          / {point.maxOccupancy}
                        </span>
                      </div>
                      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mt-2" data-id="element-3567" data-mp="jv97wkb4yl40">
                        <div className={`h-full ${rate >= 85 ? 'bg-amber-400' : 'bg-[#4F46E5]'}`} style={{
                  width: `${rate}%`
                }} data-id="element-3568" data-mp="evd86x9h1fu9" />
                      </div>
                      <p className="text-xs text-slate-400 mt-2" data-id="element-3569" data-mp="148a3zlr4vm1">
                        {point.entriesToday} {t('entrées', 'entries')} ·{' '}
                        <span className="text-amber-600 font-medium" data-id="element-3570" data-mp="3na5awzvejqpw">
                          {point.deniedToday} {t('refus', 'denied')}
                        </span>
                      </p>
                    </> : <p className="text-xs text-slate-500 pt-2" data-id="element-3571" data-mp="3i3qbejnyrutv">
                      {t('Lecteur injoignable — accès géré manuellement au comptoir.', 'Reader unreachable — access handled manually at the counter.')}
                    </p>}
                </div>;
        })}
          </div>

          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-id="element-3572" data-mp="2zfbpgasi76zx">
            <div className="px-5 py-4 border-b border-slate-100" data-id="element-3573" data-mp="34clkc5ynwav5">
              <h3 className="text-base font-semibold text-[#0F172A]" data-id="element-3574" data-mp="3943bk2avux2x">
                {t('Journal des accès', 'Access log')}
              </h3>
              <p className="text-xs text-slate-500 mt-0.5" data-id="element-3575" data-mp="2bo65bjfj5sjt">
                {t('Validation de la carte citoyenne à l’entrée', 'Citizen card validation at the door')}
              </p>
            </div>
            <div className="overflow-x-auto" data-id="element-3576" data-mp="3qt6zy3hm8sxu">
              <table className="w-full text-sm text-left" data-id="element-3577" data-mp="3qui1rxri3qmb">
                <thead className="text-xs text-slate-500 uppercase bg-slate-50" data-id="element-3578" data-mp="8ntzsgrqzjkv">
                  <tr data-id="element-3579" data-mp="3o7m6680c1bj">
                    <th className="px-5 py-3" data-id="element-3580" data-mp="3ny5584xx4hz">{t('Heure', 'Time')}</th>
                    <th className="px-5 py-3" data-id="element-3581" data-mp="hnz4uk9gy5vv">{t('Citoyen', 'Citizen')}</th>
                    <th className="px-5 py-3" data-id="element-3582" data-mp="hlw5vaqc92f0">{t('Installation', 'Venue')}</th>
                    <th className="px-5 py-3" data-id="element-3583" data-mp="dlvtdvmsv1al">{t('Droit d’accès', 'Entitlement')}</th>
                    <th className="px-5 py-3" data-id="element-3584" data-mp="rcqweramao69">{t('Résultat', 'Result')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100" data-id="element-3585" data-mp="mfh7rdv1ygjh">
                  {ACCESS_EVENTS.filter(e => {
                const q = query.trim().toLowerCase();
                return q ? e.citizen.toLowerCase().includes(q) || e.cardId.toLowerCase().includes(q) : true;
              }).map(event => <tr key={event.id} className="hover:bg-slate-50/60" data-id="element-3586" data-mp="1jzqjnnavtztl">
                      <td className="px-5 py-3.5 font-medium text-slate-700 tabular-nums" data-id="element-3587" data-mp="161ac5u5745ae">
                        {event.time}
                      </td>
                      <td className="px-5 py-3.5" data-id="element-3588" data-mp="1b1evdrxhne4b">
                        <p className="font-medium text-slate-900" data-id="element-3589" data-mp="w9mps7nhxefs">{event.citizen}</p>
                        <p className="text-xs text-slate-400" data-id="element-3590" data-mp="111nzblta4r24">{event.cardId}</p>
                      </td>
                      <td className="px-5 py-3.5 text-slate-600" data-id="element-3591" data-mp="nasq28evc7y0">
                        {event.venue[lang]}
                      </td>
                      <td className="px-5 py-3.5 text-slate-600" data-id="element-3592" data-mp="s9cag4dtdbo9">
                        {event.pass[lang]}
                      </td>
                      <td className="px-5 py-3.5" data-id="element-3593" data-mp="ian1x27t53ko">
                        {event.result === 'granted' ? <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border bg-emerald-50 text-emerald-700 border-emerald-200" data-id="element-3594" data-mp="e70mjxzil4ft">
                            <CheckIcon className="w-3.5 h-3.5" data-id="element-3595" data-mp="o4okewsf2s27" />
                            {t('Accès accordé', 'Access granted')}
                          </span> : <div data-id="element-3596" data-mp="x5cytslq2ufm">
                            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border bg-red-50 text-red-700 border-red-200" data-id="element-3597" data-mp="x64u12beqq5d">
                              <XIcon className="w-3.5 h-3.5" data-id="element-3598" data-mp="16v6ccz5fdjmi" />
                              {t('Accès refusé', 'Access denied')}
                            </span>
                            {event.reason && <p className="text-xs text-slate-500 mt-1" data-id="element-3599" data-mp="1fwcsh745wsf8">
                                {event.reason[lang]}
                              </p>}
                          </div>}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>
        </div>}
    </div>;
}